import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import CircleCropPreview from "./CircleCropPreview";
import CircleCropPrepControls from "./CircleCropPrepControls";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import ToolPrepSourceCards from "../../../shared/ToolPrepSourceCards";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useToolPrepDownloadFlow } from "../../../hooks/useToolPrepDownloadFlow";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { exportCircleCropPrepImageBlob } from "../../../lib/exportCroppedImageToPngBlob";
import { isFullCropRect } from "../../../../../components/shared/imageToolbar/imageCropModel";
import {
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  TOOL_PREP_RESULT_FOOTER_HINT,
} from "../../../../../utils/editorEntrySession";
import { useImageNaturalSize } from "../../../lib/toolPrepImageLayout";
import { withMinVisibleDuration } from "../../../lib/toolPrepCropApply";
import {
  computeCircleImageFrameForZoom,
  createDefaultCircleCropPrepSettings,
  CIRCLE_CROP_FIXED_FRAME,
  CIRCLE_CROP_BG_TYPES,
  fitImageFrameToCircleCropHost,
  getCircleCropSourceRect,
  resolveCircleCropPrepFrames,
} from "../../../lib/circleCropPrepModel";
import "./CircleCropToolPrep.css";

const CIRCLE_CROP_THANK_YOU_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete: "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
  previewAlt: "Circle cropped image download",
  primaryActionLabel: "Add new image",
};

/**
 * Editing Tools → Circle Crop prep: circular mask, zoom.
 * Format/quality are chosen in Download Options after crop is applied.
 */
const CircleCropToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  removeBgDefault = false,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const [cropSettings, setCropSettings] = useState(() =>
    createDefaultCircleCropPrepSettings()
  );
  const [removeBg, setRemoveBg] = useState(Boolean(removeBgDefault));
  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const [cropApplied, setCropApplied] = useState(false);
  const [cropBusy, setCropBusy] = useState(false);
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState(null);
  const [cropApplyError, setCropApplyError] = useState(null);
  const bgRequestRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const croppedPreviewUrlRef = useRef(null);
  const previewHostRef = useRef({ width: 0, height: 0 });

  const {
    sourceImageUrl,
    resolvedFile,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
    sourceFileName,
    sourceFileSizeLabel,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy || cropBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      if (!removeBgDefault) setRemoveBg(false);
      setBgError(null);
      setCropApplyError(null);
      setCropSettings(createDefaultCircleCropPrepSettings());
      setCropApplied(false);
      if (croppedPreviewUrlRef.current) {
        URL.revokeObjectURL(croppedPreviewUrlRef.current);
        croppedPreviewUrlRef.current = null;
      }
      setCroppedPreviewUrl(null);
    },
  });

  const downloadFlow = useToolPrepDownloadFlow({
    variant: "editing",
    sourceFileName,
    fallbackDownloadName: "circle-crop.png",
    getToken,
    toolKey: "circle_crop",
    shareTitle: "Circle Crop tool",
    shareText:
      "I cropped my photo into a perfect circle with LazyStudio's Circle Crop tool!",
    thankYouCopy: CIRCLE_CROP_THANK_YOU_COPY,
  });

  const { downloadBusy } = downloadFlow;

  useEffect(() => {
    if (cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
  }, [cachedCutoutUrl]);

  const previewImageUrl = removeBg && cutoutUrl ? cutoutUrl : sourceImageUrl;
  const naturalSize = useImageNaturalSize(previewImageUrl);

  const fitImageToPreviewHost = useCallback(
    (hostWidth, hostHeight, imageWidth, imageHeight) => {
      if (!hostWidth || !hostHeight || !imageWidth || !imageHeight) return null;
      return fitImageFrameToCircleCropHost(
        CIRCLE_CROP_FIXED_FRAME,
        imageWidth,
        imageHeight,
        hostWidth,
        hostHeight
      );
    },
    []
  );

  const handlePreviewHostMetrics = useCallback(
    ({ hostWidth, hostHeight, naturalWidth, naturalHeight }) => {
      previewHostRef.current = { width: hostWidth, height: hostHeight };
      const imageFrame = fitImageToPreviewHost(
        hostWidth,
        hostHeight,
        naturalWidth,
        naturalHeight
      );
      if (!imageFrame) return;
      setCropSettings((prev) => ({
        ...prev,
        cropFrame: CIRCLE_CROP_FIXED_FRAME,
        imageFrame,
      }));
    },
    [fitImageToPreviewHost]
  );

  useEffect(() => {
    if (!naturalSize.width || !naturalSize.height || !previewImageUrl) return;
    const { width: hostWidth, height: hostHeight } = previewHostRef.current;
    const imageFrame = fitImageToPreviewHost(
      hostWidth,
      hostHeight,
      naturalSize.width,
      naturalSize.height
    );
    if (!imageFrame) return;
    setCropSettings((prev) => ({
      ...prev,
      cropFrame: CIRCLE_CROP_FIXED_FRAME,
      imageFrame,
    }));
  }, [
    fitImageToPreviewHost,
    naturalSize.height,
    naturalSize.width,
    previewImageUrl,
  ]);

  useSyncScrollbarWidth(scrollBodyRef, [
    sourceImageUrl,
    cropSettings.zoom,
    cropSettings.rotation,
    cropSettings.flipH,
    cropSettings.flipV,
    cropSettings.backgroundType,
    cropSettings.backgroundColor,
    cropSettings.borderWidth,
    cropSettings.borderColor,
    cropSettings.imageFrame?.x,
    cropSettings.imageFrame?.y,
    cropSettings.imageFrame?.w,
    cropSettings.imageFrame?.h,
  ]);

  const handleSettingsChange = (nextSettings) => {
    const visualKeys = [
      "zoom",
      "rotation",
      "flipH",
      "flipV",
      "backgroundType",
      "backgroundColor",
      "borderWidth",
      "borderColor",
      "borderColorCustom",
    ];
    const cropDirty = visualKeys.some(
      (key) =>
        nextSettings[key] !== undefined && nextSettings[key] !== cropSettings[key]
    );
    if (cropDirty) {
      setCropApplied(false);
    }

    setCropSettings((prev) => {
      const next = { ...prev, ...nextSettings, cropFrame: CIRCLE_CROP_FIXED_FRAME };
      const zoomChanged =
        nextSettings.zoom !== undefined && nextSettings.zoom !== prev.zoom;
      if (zoomChanged) {
        const { width: hostWidth, height: hostHeight } = previewHostRef.current;
        const coverFrame = fitImageFrameToCircleCropHost(
          CIRCLE_CROP_FIXED_FRAME,
          naturalSize.width,
          naturalSize.height,
          hostWidth,
          hostHeight
        );
        const resolved = resolveCircleCropPrepFrames(
          CIRCLE_CROP_FIXED_FRAME,
          computeCircleImageFrameForZoom(
            next.zoom,
            CIRCLE_CROP_FIXED_FRAME,
            prev.imageFrame,
            coverFrame
          )
        );
        next.cropFrame = CIRCLE_CROP_FIXED_FRAME;
        next.imageFrame = resolved.imageFrame;
      }
      return next;
    });
  };

  const runBackgroundRemoval = useCallback(async () => {
    if (cutoutUrl?.startsWith("blob:") || cutoutUrl?.startsWith("data:")) {
      return { cutoutUrl, creationId };
    }

    const requestId = Symbol("bg");
    bgRequestRef.current = requestId;
    setBgLoading(true);
    setBgError(null);

    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      if (bgRequestRef.current !== requestId) return null;
      setCutoutUrl(nextUrl);
      if (cid != null) setCreationId(cid);
      onCutoutReady?.(nextUrl);
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      if (bgRequestRef.current === requestId) {
        setBgError(err?.message || "Failed to remove background");
      }
      throw err;
    } finally {
      if (bgRequestRef.current === requestId) {
        setBgLoading(false);
      }
    }
  }, [
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    onCutoutReady,
    resolvedFile,
    sourceImageUrl,
  ]);

  const handleRemoveBgToggle = async () => {
    if (!sourceImageUrl || bgLoading) return;
    const next = !removeBg;
    setRemoveBg(next);
    if (!next) {
      setBgError(null);
      setCropApplied(false);
      setCropSettings((prev) => ({
        ...prev,
        backgroundType: CIRCLE_CROP_BG_TYPES.TRANSPARENT,
      }));
      return;
    }
    try {
      await runBackgroundRemoval();
    } catch {
      setRemoveBg(false);
    }
  };

  const handleFramesChange = (_cropFrame, imageFrame) => {
    setCropApplied(false);
    const resolved = resolveCircleCropPrepFrames(
      CIRCLE_CROP_FIXED_FRAME,
      imageFrame
    );
    setCropSettings((prev) => ({
      ...prev,
      cropFrame: CIRCLE_CROP_FIXED_FRAME,
      imageFrame: resolved.imageFrame,
    }));
  };

  const buildExportSettings = useCallback(
    () => ({
      ...(removeBg
        ? cropSettings
        : {
            ...cropSettings,
            backgroundType: CIRCLE_CROP_BG_TYPES.TRANSPARENT,
          }),
      outputFormat: "png",
    }),
    [cropSettings, removeBg]
  );

  const handleApplyCrop = async () => {
    const url = previewImageUrl;
    if (!url || cropBusy) return;

    setCropApplyError(null);
    setCropBusy(true);
    try {
      const blob = await withMinVisibleDuration(
        exportCircleCropPrepImageBlob(url, buildExportSettings())
      );
      if (croppedPreviewUrlRef.current) {
        URL.revokeObjectURL(croppedPreviewUrlRef.current);
      }
      const nextUrl = URL.createObjectURL(blob);
      croppedPreviewUrlRef.current = nextUrl;
      setCroppedPreviewUrl(nextUrl);
      setCropApplied(true);
    } catch (err) {
      setCropApplyError(err?.message || "Could not apply crop.");
    } finally {
      setCropBusy(false);
    }
  };

  const handleAdjustCrop = () => {
    setCropApplyError(null);
    setCropApplied(false);
    if (croppedPreviewUrlRef.current) {
      URL.revokeObjectURL(croppedPreviewUrlRef.current);
      croppedPreviewUrlRef.current = null;
    }
    setCroppedPreviewUrl(null);
  };

  const resolveOutputImageUrl = useCallback(async (imageUrl, settings) => {
    const sourceRect = getCircleCropSourceRect(settings);
    if (!imageUrl || isFullCropRect(sourceRect)) {
      return imageUrl;
    }
    const blob = await exportCircleCropPrepImageBlob(imageUrl, {
      ...settings,
      outputFormat: "png",
    });
    return URL.createObjectURL(blob);
  }, []);

  const handleDownload = useCallback(() => {
    if (!croppedPreviewUrl) return;
    downloadFlow.openDownloadOptions({
      previewUrl: croppedPreviewUrl,
      resolveImageUrl: async () => croppedPreviewUrl,
    });
  }, [croppedPreviewUrl, downloadFlow]);

  useEffect(
    () => () => {
      if (croppedPreviewUrlRef.current) {
        URL.revokeObjectURL(croppedPreviewUrlRef.current);
        croppedPreviewUrlRef.current = null;
      }
    },
    []
  );

  const controlsLocked =
    cropApplied || cropBusy || bgLoading || doneBusy || downloadBusy;

  const handleDone = async () => {
    setDoneBusy(true);
    try {
      let imageUrl = sourceImageUrl;
      let doneCreationId = creationId;
      if (removeBg) {
        if (cutoutUrl) {
          imageUrl = cutoutUrl;
        } else {
          const removed = await runBackgroundRemoval();
          imageUrl = removed?.cutoutUrl;
          doneCreationId = removed?.creationId ?? doneCreationId;
        }
      }
      if (!imageUrl) {
        throw new Error("No image ready");
      }
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:")
      ) {
        throw new Error(
          "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
        );
      }

      const finalUrl = await resolveOutputImageUrl(imageUrl, cropSettings);
      onDone({
        imageUrl: finalUrl,
        cropSettings: { ...cropSettings },
        removeBg,
        creationId: removeBg ? doneCreationId : null,
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish circle crop setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const leftContent = (
    <div className="ccrp-panel">
      <div className="ccrp-scroll-body tool-prep-scroll" ref={scrollBodyRef}>
        <div className="ccrp-source-block">
          <ToolPrepSourceCards
            originalImageUrl={sourceImageUrl}
            previewImageUrl={removeBg && cutoutUrl ? cutoutUrl : null}
            removeBgActive={removeBg}
            cutoutPending={bgLoading && !cutoutUrl}
            onUploadClick={openUploadPicker}
            onRemove={clearSource}
            uploadProgress={uploadProgress}
            sourceName={sourceFileName}
            sourceSizeLabel={sourceFileSizeLabel}
            actionsDisabled={bgLoading || doneBusy || downloadBusy}
            removeBgControls={{
              active: removeBg,
              onToggle: handleRemoveBgToggle,
              disabled: bgLoading || !sourceImageUrl || cropApplied || cropBusy,
              locked: cropApplied,
              bio: "Optional — remove the backdrop before circle cropping the subject.",
              error: bgError,
            }}
          />
        </div>
        <CircleCropPrepControls
          settings={cropSettings}
          onChange={handleSettingsChange}
          disabled={!sourceImageUrl || controlsLocked}
          removeBgActive={removeBg}
        />
      </div>
    </div>
  );

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey="circle_crop"
        title="Circle Crop"
        left={leftContent}
        preview={
          <CircleCropPreview
            imageUrl={previewImageUrl}
            settings={
              removeBg
                ? cropSettings
                : {
                    ...cropSettings,
                    backgroundType: CIRCLE_CROP_BG_TYPES.TRANSPARENT,
                  }
            }
            loading={bgLoading}
            applying={cropBusy}
            resultMode={cropApplied}
            resultImageUrl={croppedPreviewUrl}
            disabled={controlsLocked}
            onFramesChange={handleFramesChange}
            onHostMetricsChange={handlePreviewHostMetrics}
            emptyLabel={
              uploadProgress != null && !previewImageUrl
                ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                : undefined
            }
          />
        }
        onDone={cropApplied ? handleDone : handleApplyCrop}
        onCancel={onCancel}
        confirmClose={Boolean(sourceImageUrl)}
        onDownload={cropApplied ? handleDownload : null}
        onOpenInEditor={cropApplied ? handleDone : null}
        onSecondaryAction={cropApplied ? handleAdjustCrop : null}
        secondaryActionLabel="Adjust crop"
        busy={cropBusy || doneBusy || bgLoading || downloadBusy}
        busyLabel="Cropping…"
        showDone={!cropApplied}
        doneDisabled={!sourceImageUrl || cropBusy}
        doneLabel="Crop"
        footerActionOrder={
          cropApplied
            ? ["cancel", "secondary", "download", "openInEditor"]
            : ["cancel", "primary"]
        }
        openInEditorLabel="Open in Editor"
        footerHint={
          cropApplyError ||
          (cropApplied
            ? TOOL_PREP_RESULT_FOOTER_HINT
            : "Drag the image inside the circle to position it, then click Crop.")
        }
        footerHintClassName={
          cropApplyError ? "tool-prep-popup__footer-hint--error" : ""
        }
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey="circle_crop"
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default CircleCropToolPrep;
